import React, { useEffect } from 'react';

import { Radio, Spin, Tag } from '@arco-design/web-react';
import { get } from 'lodash';

import { EmptyTips } from '@/components/EmptyTips';
import { defaultRender } from '@/components/FieldsCard';

import styles from './index.module.less';

export interface RadioListColumn {
  title: string;
  dataIndex: string;
  render?: (value: any, item: Record<string, any>) => React.ReactNode;
}

interface CardItemProps {
  item: Record<string, any>;
  firstColumn: RadioListColumn | undefined;
  restColumns: RadioListColumn[];
  isSelected: boolean;
  disabled?: boolean;
  onChange?: (value: string, item?: Record<string, any>, isClick?: boolean) => void;
}

function CardItem({ item, firstColumn, restColumns, isSelected, disabled, onChange }: CardItemProps) {
  const titleVal = firstColumn
    ? firstColumn.render
      ? firstColumn.render(get(item, firstColumn.dataIndex, '-'), item)
      : defaultRender(get(item, firstColumn.dataIndex), item)
    : (item.key ?? '-');

  const handleClick = () => {
    if (disabled) return;
    onChange?.(item.key, item, true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !disabled) onChange?.(item.key, item, true);
  };

  return (
    <div
      onClick={handleClick}
      className={`${styles.card} ${isSelected ? styles.selected : ''}`}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.cardRadio}>
        <Radio disabled={disabled} checked={isSelected} />
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardTitle}>{titleVal}</div>
        {restColumns.length > 0 && (
          <div className={styles.cardTags}>
            {restColumns.map((col, idx) => {
              const val = get(item, col.dataIndex);
              if (!val) return null;
              const valueContent = col.render ? col.render(val, item) : defaultRender(val, item);
              return (
                <Tag key={idx} size="small" bordered>
                  {col.title}：{valueContent}
                </Tag>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

interface Props {
  disabled?: boolean;
  columns: RadioListColumn[];
  data: Record<string, any>[];
  loading?: boolean;
  value?: string;
  onChange?: (value: string, item?: Record<string, any>, isClick?: boolean) => void;
  style?: React.CSSProperties;
}

export function RadioList({ disabled, columns, data, loading, value, onChange, style }: Props) {
  useEffect(() => {
    if (!value) return;
    onChange?.(
      value,
      data?.find((i) => i.key === value),
    );
  }, [value, data, onChange]);

  const firstColumn = columns[0];
  const restColumns = columns.slice(1);

  return (
    <Spin loading={loading} block>
      <div className={styles.list_content} style={style}>
        {data?.length > 0 ? (
          <div className={styles.cardList}>
            {data.map((item) => (
              <CardItem
                key={item.key}
                item={item}
                firstColumn={firstColumn}
                restColumns={restColumns}
                isSelected={value === item.key}
                disabled={disabled}
                onChange={onChange}
              />
            ))}
          </div>
        ) : (
          <EmptyTips />
        )}
      </div>
    </Spin>
  );
}
